import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { addWeeks, format, parse, startOfDay } from 'date-fns';

import {
  schedulingV4BookSelfScheduledAppointmentMutation,
  schedulingV4VerifyPatientBookingMutation,
} from '@/api/generated/@tanstack/react-query.gen';
import {
  AvailabilityCalendar,
  type AvailabilityCalendarFormData,
} from '@/components/AvailabilityCalendar';
import { ErrorModal } from '@/components/ErrorModal';
import { NavigationBar } from '@/components/NavigationBar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DEFAULT_TELEHEALTH_CONTACT_TYPE,
  NEW_PATIENT_VISIT_DURATION_IN_MINUTES,
} from '@/lib/constants';
import { logger } from '@/lib/logger';
import type { Appointment } from '@/types/order.type';

import { SelfScheduleConfirmation } from './components/SelfScheduleConfirmation';
import { SelfScheduleVerifyDobStep } from './components/SelfScheduleVerifyDobStep';

const toApiDob = (dob: string) => {
  const parsed = parse(dob, 'MM/dd/yyyy', new Date());
  return format(parsed, 'yyyy-MM-dd');
};

interface VerifiedContext {
  dob: string;
  patientState: string | null;
}

const SelfScheduleStep = {
  VERIFY_DOB: 'verify_dob',
  SELECT_SLOT: 'select_slot',
  CONFIRMATION: 'confirmation',
} as const;

type SelfScheduleStepValue = (typeof SelfScheduleStep)[keyof typeof SelfScheduleStep];

const STEPS: SelfScheduleStepValue[] = [
  SelfScheduleStep.VERIFY_DOB,
  SelfScheduleStep.SELECT_SLOT,
  SelfScheduleStep.CONFIRMATION,
];

const PRIORITY_APPOINTMENT_TYPE_ID = import.meta.env
  .VITE_HEALTHIE_APPOINTMENT_TYPE_NEW_PATIENT_VISIT_ID;
const PRIORITY_CONTACT_TYPE = DEFAULT_TELEHEALTH_CONTACT_TYPE;

export const SelfScheduleAppointment = () => {
  const { orderPublicId } = useParams({
    from: '/appLayout/wizardLayout/appointment/schedule/$orderPublicId',
  });

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const currentStep = STEPS[currentStepIndex];

  const [verifiedContext, setVerifiedContext] = useState<VerifiedContext | null>(null);
  const [appointmentDetails, setAppointmentDetails] = useState<Appointment | null>(null);
  const [_patientPhoneNumber, setPatientPhoneNumber] = useState('');
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  const verifyMutation = useMutation({
    ...schedulingV4VerifyPatientBookingMutation(),
    retry: false,
  });
  const bookMutation = useMutation({
    ...schedulingV4BookSelfScheduledAppointmentMutation(),
    retry: false,
  });

  const appointmentDuration = NEW_PATIENT_VISIT_DURATION_IN_MINUTES;

  const startDate = startOfDay(new Date());
  const endDate = startOfDay(addWeeks(startDate, 2));

  const handleVerifyDob = async (dob: string) => {
    try {
      const result = await verifyMutation.mutateAsync({
        body: {
          order_public_id: orderPublicId,
          patient_dob: toApiDob(dob),
        },
      });
      setVerifiedContext({ dob, patientState: result.payload?.patient_state ?? null });
      setCurrentStepIndex(1);
    } catch {
      // Error state handled by verifyMutation.isError in the UI
    }
  };

  const handleSlotSubmit = async (data: AvailabilityCalendarFormData) => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    try {
      if (
        !data.selectedSlot ||
        !data.selectedProviderId ||
        !data.selectedContactType ||
        !data.selectedAppointmentTypeId
      ) {
        throw new Error(
          'Selected slot, provider ID, contact type, or appointment type ID is missing'
        );
      }

      const result = await bookMutation.mutateAsync({
        body: {
          order_public_id: orderPublicId,
          patient_dob: toApiDob(verifiedContext!.dob),
          appointment_date: data.selectedSlot,
          provider_id: data.selectedProviderId,
          timezone,
          appointment_type_id: data.selectedAppointmentTypeId,
          telehealth_contact_type: data.selectedContactType,
        },
      });

      setAppointmentDetails({
        scheduledTime: result.payload.date ? new Date(result.payload.date) : undefined,
        timeZone: timezone,
        durationInMinutes: appointmentDuration,
        provider: { name: null, phoneNumber: '' },
      });
      setPatientPhoneNumber(result.payload.phone_number ?? '');
      setCurrentStepIndex(2);
    } catch (err) {
      logger.error('Failed to book appointment', { error: err, data, orderPublicId });
      setIsErrorModalOpen(true);
    }
  };

  return (
    <>
      {currentStep === SelfScheduleStep.VERIFY_DOB && (
        <SelfScheduleVerifyDobStep
          onSubmit={handleVerifyDob}
          isPending={verifyMutation.isPending}
          error={
            verifyMutation.isError
              ? 'We could not verify your identity. Please check your date of birth and try again.'
              : undefined
          }
        />
      )}

      {currentStep === SelfScheduleStep.SELECT_SLOT && verifiedContext && (
        <>
          <NavigationBar onBack={() => setCurrentStepIndex(0)} backLabel="Back" />
          <Card className="gap-6 sm:gap-6">
            <CardHeader>
              <CardTitle>Schedule a {appointmentDuration} min clinician phone call</CardTitle>
              <CardDescription>
                <p className="text-orchid-600 font-medium">Important note</p>
                <p>Patient must be present for Telehealth call</p>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AvailabilityCalendar
                startDate={startDate}
                endDate={endDate}
                licensedInState={verifiedContext.patientState}
                onSubmit={handleSlotSubmit}
                priorityAppointmentTypeId={PRIORITY_APPOINTMENT_TYPE_ID}
                priorityContactType={PRIORITY_CONTACT_TYPE}
              />
            </CardContent>
          </Card>
        </>
      )}

      {currentStep === SelfScheduleStep.CONFIRMATION && appointmentDetails && (
        <SelfScheduleConfirmation appointment={appointmentDetails} />
      )}

      {isErrorModalOpen && (
        <ErrorModal
          isOpen={isErrorModalOpen}
          setIsOpen={setIsErrorModalOpen}
          title="Something went wrong"
          description="We're having trouble booking your appointment. Please try again."
        />
      )}
    </>
  );
};
